import { gql } from '@apollo/client'

/**
 * Query to get the shipment details
 * @param id is the unique id of the shipment
 */
export const CHAT_SHIPMENT_DETAILS = gql`
  query MyShipment($query: ID!) {
    myShipment(input: { id: $query }) {
      carriers
      customer {
        name
      }
      drivers {
        user {
          id
          countryCode
          name
          username
          email
          phone
          photo
          phoneNumberInfo {
            canReceiveSMS
            carrierName
            carrierType
            carrierTypeDescription
            isValid
          }
        }
      }
      dispatchers {
        user {
          id
          countryCode
          name
          username
          email
          phone
          photo
        }
      }
      equipment
      id
      isShipmentOwner
      destination
      origin
      shipmentNumber
      trailerNumber
      truckNumber
      shipmentStatus
      trackingMapURL
      shipmentMode
      tmsURL
      userRole
      shipmentOwnerWorkspace {
        id
        displayName
        workspacename
        logo
      }
      workspace {
        id
        displayName
        workspacename
      }
      teamMembers {
        id
        name
        username
        email
        phone
        photo
        emailOrPhone
      }
      stops {
        id
        warehouse {
          name
          category
          geofence {
            property {
              latitude
              longitude
            }
            radius
            type
          }
          rating
          reviews
          placeID
          thumbnail
          warehouseHours
          address {
            addressLabel
            boundingBox
            streetNumber
            street
            city
            state
            stateCode
            county
            postalCode
            postalCodeSuffix
            country
            countryCode
            coordinates {
              latitude
              longitude
            }
          }
        }
        appointment {
          start {
            date
            time
          }
          end {
            date
            time
          }
          isFirstComeFirstServe
        }
        referenceNumber
        type
        sequence
        instructions
        eta {
          date
          time
        }
        arrivalDateTime {
          date
          time
        }
        departureDateTime {
          date
          time
        }
      }
    }
  }
`

export const GET_ASSIGNED_SHIPMENTS = gql`
  query MyShipments($input: ShipmentQueryInput) {
    myShipments(input: $input) {
      paginationData {
        hasNextPage
        paginationKey
        dataSource
      }
      shipments {
        created
        createdVia
        customer {
          name
        }
        lastLocation {
          location {
            city
            countryCode
            nextStopDetails {
              distanceToNextStop {
                unit
                value
              }
              etaToNextStop {
                date
                time
                timezone
              }
              sequence
              stopExternalID
              stopID
              stopType
            }
            postalCode
            stateCode
            timestamp
            coordinates {
              latitude
              longitude
            }
          }
          nextStopDetails {
            distanceToNextStop {
              unit
              value
            }
            etaToNextStop {
              date
              time
              timezone
            }
            sequence
            stopExternalID
            stopID
            stopType
          }
          source
        }
        destination
        carriers
        dispatchers {
          membership {
            accessType
            role
          }
          user {
            email
            id
            name
            phone
            photo
          }
          workspace {
            displayName
            id
            logo
            workspacename
          }
        }
        mobileAppTrackingStatus
        drivers {
          membership {
            accessType
            role
          }
          user {
            email
            id
            name
            phone
            photo
          }
          workspace {
            displayName
            id
            logo
            workspacename
          }
        }
        id
        isShipmentOwner
        origin
        priority
        release
        shipmentMode
        shipmentNumber
        trailerNumber
        truckNumber
        areMobileAppPermissionsCorrect
        isTracking
        isTrackingViaAPI
        isTrackingViaDriverMobileApp
        isTrackingViaELD
        isTrackingViaExternalTrackingLink
        isTrackingViaSFTP
        isTrackingViaTMSApp
        isTrackingViaTiveDevice
        isTrackingViaVisibilityProvider
        trackingEndTime
        trackingStartTime
        shipmentOwnerWorkspace {
          displayName
          id
          logo
          workspacename
        }
        shipmentStatus
        stops {
          id
          sequence
          appointment {
            start {
              date
              time
              timezone
            }
            end {
              date
              time
              timezone
            }
            isFirstComeFirstServe
          }
        }
        teamMembers {
          email
          emailOrPhone
          id
          name
          phone
          photo
        }
        trackingMapURL
        userRole
        workspace {
          displayName
          id
          workspacename
        }
      }
    }
  }
`

/**
 * Changes the settings for a user's shipments page
 */
export const SET_USER_SHIPMENTS_PAGE_SETTINGS = gql`
  mutation SetUserShipmentsPageSettings($hideNeedsCarrierShipments: Boolean!) {
    setUserShipmentsPageSettings(input: { hideNeedsCarrierShipments: $hideNeedsCarrierShipments }) {
      id
      shipmentsPageSettings {
        hideNeedsCarrierShipments
      }
    }
  }
`
/**
 * Changes the settings for a workspace's shipments page
 */
export const SET_WORKSPACE_SHIPMENTS_PAGE_SETTINGS = gql`
  mutation SetWorkspaceShipmentsPageSettings($hideNeedsCarrierShipments: Boolean!) {
    setWorkspaceShipmentsPageSettings(input: { hideNeedsCarrierShipments: $hideNeedsCarrierShipments }) {
      id
      shipmentsPageSettings {
        hideNeedsCarrierShipments
      }
    }
  }
`

/**
 * Query to get the shipment details
 * @param id is the unique id of the shipment
 */
export const MY_SHIPMENT = gql`
  query MyShipment($id: ID!) {
    myShipment(input: { id: $id }) {
      id
      createdVia
      shipmentNumber
      tmsURL
      shipmentStatus
      trackingMapURL
      shipmentMode
      userRole
      isShipmentOwner
      priority
      release
      files
      workspace {
        id
        displayName
        workspacename
      }
      shipmentOwnerWorkspace {
        enterpriseWorkspaceID
        id
        displayName
        workspacename
        logo
      }
      commodity
      equipment
      referenceNumbers {
        billOfLading
        billingReferenceNumber
        externalTrackingID
        orderNumber
        progressiveRotatingOrderNumber
        purchaseOrderNumber
      }
      teamMembers {
        id
        name
        username
        email
        phone
        photo
        emailOrPhone
      }
      drivers {
        user {
          id
          name
          email
          phone
          photo
          phoneNumberInfo {
            canReceiveSMS
            carrierName
            carrierType
            carrierTypeDescription
            isValid
          }
        }
        workspace {
          id
          displayName
          workspacename
          logo
        }
        membership {
          role
          accessType
        }
      }
      dispatchers {
        user {
          id
          name
          email
          phone
          photo
        }
        workspace {
          id
          displayName
          workspacename
          logo
        }
        membership {
          role
          accessType
        }
      }
      weight {
        value
        unit
      }
      distance {
        value
        unit
      }
      tmsURL
      stops {
        id
        warehouse {
          name
          category
          geofence {
            property {
              latitude
              longitude
            }
            radius
            type
          }
          rating
          reviews
          placeID
          locationID
          thumbnail
          warehouseHours
          address {
            addressLabel
            boundingBox
            streetNumber
            street
            city
            state
            stateCode
            county
            postalCode
            postalCodeSuffix
            country
            countryCode
            coordinates {
              latitude
              longitude
            }
          }
        }
        appointment {
          start {
            date
            time
          }
          end {
            date
            time
          }
          isFirstComeFirstServe
        }
        referenceNumber
        type
        sequence
        instructions
        eta {
          date
          time
        }
        arrivalDateTime {
          date
          time
        }
        departureDateTime {
          date
          time
        }
      }
    }
  }
`

/**
 * Query to get the shipment details
 * @param id is the unique id of the shipment
 */
export const WORKSPACE_SHIPMENT = gql`
  query WorkspaceShipment($id: ID!) {
    workspaceShipment(input: { id: $id }) {
      id
      createdVia
      shipmentNumber
      shipmentStatus
      trackingMapURL
      shipmentMode
      userRole
      isShipmentOwner
      postID
      priority
      release
      trailerNumber
      truckNumber
      files
      workspace {
        id
        displayName
        workspacename
      }
      shipmentOwnerWorkspace {
        enterpriseWorkspaceID
        id
        displayName
        workspacename
        logo
      }
      teamMembers {
        id
        name
        username
        email
        phone
        photo
        emailOrPhone
      }
      tmsURL
      drivers {
        user {
          id
          name
          email
          phone
          photo
          phoneNumberInfo {
            canReceiveSMS
            carrierName
            carrierType
            carrierTypeDescription
            isValid
          }
        }
        workspace {
          id
          displayName
          workspacename
          logo
        }
        membership {
          role
          accessType
        }
      }
      commodity
      equipment
      referenceNumbers {
        billOfLading
        billingReferenceNumber
        externalTrackingID
        orderNumber
        progressiveRotatingOrderNumber
        purchaseOrderNumber
      }
      dispatchers {
        user {
          id
          name
          email
          phone
          photo
        }
        workspace {
          id
          displayName
          workspacename
          logo
        }
        membership {
          role
          accessType
        }
      }
      weight {
        value
        unit
      }
      distance {
        value
        unit
      }
      stops {
        id
        warehouse {
          name
          category
          geofence {
            property {
              latitude
              longitude
            }
            radius
            type
          }
          rating
          reviews
          placeID
          locationID
          thumbnail
          warehouseHours
          address {
            addressLabel
            boundingBox
            streetNumber
            street
            city
            state
            stateCode
            county
            postalCode
            postalCodeSuffix
            country
            countryCode
            coordinates {
              latitude
              longitude
            }
          }
        }
        appointment {
          start {
            date
            time
          }
          end {
            date
            time
          }
          isFirstComeFirstServe
        }
        referenceNumber
        type
        sequence
        instructions
        eta {
          date
          time
        }
        arrivalDateTime {
          date
          time
        }
        departureDateTime {
          date
          time
        }
      }
    }
  }
`

/**
 * Get a shipment's activity
 * @param {activities: ShipmentActivityReturnType[]} activities - The activities of the shipment
 */
export const SHIPMENT_ACTIVITY = gql`
  query ShipmentActivity($shipmentID: ID!) {
    shipmentActivity(input: { shipmentID: $shipmentID }) {
      actionByDisplayName
      actionByUser {
        name
      }
      actionType
      displayText
      eventISODateTime
      eventName
      field
      value {
        text
        user {
          name
        }
        workspace {
          displayName
        }
      }
    }
  }
`

export const GET_WORKSPACE_SHIPMENTS = gql`
  query WorkspaceShipments($input: ShipmentQueryInput) {
    workspaceShipments(input: $input) {
      paginationData {
        hasNextPage
        paginationKey
        dataSource
      }
      shipments {
        created
        createdVia
        customer {
          name
        }
        carriers
        destination
        dispatchers {
          membership {
            accessType
            role
          }
          user {
            email
            id
            name
            phone
            photo
          }
          workspace {
            displayName
            id
            logo
            workspacename
          }
        }
        lastLocation {
          location {
            city
            countryCode
            nextStopDetails {
              distanceToNextStop {
                unit
                value
              }
              etaToNextStop {
                date
                time
                timezone
              }
              sequence
              stopExternalID
              stopID
              stopType
            }
            postalCode
            stateCode
            timestamp
            coordinates {
              latitude
              longitude
            }
          }
          nextStopDetails {
            distanceToNextStop {
              unit
              value
            }
            etaToNextStop {
              date
              time
              timezone
            }
            sequence
            stopExternalID
            stopID
            stopType
          }
          source
        }
        mobileAppTrackingStatus
        drivers {
          membership {
            accessType
            role
          }
          user {
            email
            id
            name
            phone
            photo
          }
          workspace {
            displayName
            id
            logo
            workspacename
          }
        }
        id
        isShipmentOwner
        origin
        priority
        release
        shipmentMode
        shipmentNumber
        areMobileAppPermissionsCorrect
        isTracking
        isTrackingViaAPI
        isTrackingViaDriverMobileApp
        isTrackingViaELD
        isTrackingViaExternalTrackingLink
        isTrackingViaSFTP
        isTrackingViaTMSApp
        isTrackingViaTiveDevice
        isTrackingViaVisibilityProvider
        trackingEndTime
        trackingStartTime
        shipmentOwnerWorkspace {
          displayName
          id
          logo
          workspacename
        }
        shipmentStatus
        stops {
          id
          sequence
          appointment {
            start {
              date
              time
              timezone
            }
            end {
              date
              time
              timezone
            }
            isFirstComeFirstServe
          }
        }
        teamMembers {
          email
          emailOrPhone
          id
          name
          phone
          photo
        }
        trackingMapURL
        trackingStatus {
          isTracking
          status
        }
        userRole
        workspace {
          displayName
          id
          workspacename
        }
      }
    }
  }
`

export const GET_PUBLIC_SHIPMENTS = gql`
  query PublicShipments($input: PublicShipmentQueryInput) {
    publicShipments(input: $input) {
      paginationData {
        hasNextPage
        paginationKey
        dataSource
      }
      shipments {
        created
        drivers {
          user {
            id
            name
          }
        }
        referenceNumbers {
          billOfLading
          billingReferenceNumber
          externalTrackingID
          orderNumber
          progressiveRotatingOrderNumber
          purchaseOrderNumber
        }
        isTracking
        isTrackingViaAPI
        isTrackingViaDriverMobileApp
        isTrackingViaELD
        isTrackingViaExternalTrackingLink
        isTrackingViaSFTP
        isTrackingViaTMSApp
        isTrackingViaTiveDevice
        destination
        lastLocationDeprecated {
          location {
            city
            countryCode
            nextStopDetails {
              distanceToNextStop {
                unit
                value
              }
              etaToNextStop {
                date
                time
                timezone
              }
              sequence
              stopExternalID
              stopID
              stopType
            }
            postalCode
            stateCode
            timestamp
            coordinates {
              latitude
              longitude
            }
          }
          nextStopDetails {
            distanceToNextStop {
              unit
              value
            }
            etaToNextStop {
              date
              time
              timezone
            }
            sequence
            stopExternalID
            stopID
            stopType
          }
          source
        }
        mobileAppTrackingStatus
        id
        origin
        shipmentMode
        shipmentNumber
        shipmentOwnerWorkspace {
          displayName
          id
          logo
          workspacename
        }
        shipmentStatus
        trackingEndTime
        trackingStartTime
      }
    }
  }
`
